import React, { useEffect, useRef, useState } from 'react';
import useLocalStorage from './useLocal';
import config from '../../assets/utils/config';
import { fetchCurrentUserProfile, fetchAccessModules, fetchPermissionsVersion } from '../../features/dash/rbacApi';
import { buildPermissionMap } from './permissions';
import {
    ACCESS_CATALOG_STATES,
    ACCESS_MODULES,
    getAccessCatalogState,
    getAccessModules,
    setAccessCatalogState,
    setRuntimeAccessModules,
} from './accessCatalog';

const AuthContext = React.createContext();
export function useAuth() {
    const context = React.useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be called within a AuthProvider');
    }
    return context;
}

export default function AuthProvider({ children }) {
    const [user, setUser] = useLocalStorage(config.key.user);
    const [token, setToken, ClearValues] = useLocalStorage(config.key.token);
    const [permissions, setPermissions] = useState(() => {
        // Initialize permissions from stored user profile if available
        if (user?.profile?.permissions) {
            return buildPermissionMap(user.profile);
        }
        return {};
    });
    const [accessModules, setAccessModules] = useState(ACCESS_MODULES);
    // How the catalog in `accessModules` was obtained: 'live' | 'empty' | 'unavailable'.
    // 'unavailable' is a legitimate reason to be running on the built-in list; 'live' is not —
    // a built-in used while live means an Access Modules row is not reaching this app.
    // Diagnostic only: nothing gates on this.
    const [accessCatalogState, setAccessCatalogStatus] = useState(getAccessCatalogState);
    // True once a profile/permissions fetch has RESOLVED — success or failure.
    // Lets gates tell "empty map because still loading" from "empty map because this is a
    // legacy staffRoleId account with no RBAC grants". Never gate access on it, only waiting.
    const [profileBootstrapped, setProfileBootstrapped] = useState(false);
    const profileRefreshOnce = useRef(false);
    const timerRef = useRef();
    const permissionsVersionRef = useRef(null);
    const permissionsPollRef = useRef();

    const applyAccessCatalog = (list) => {
        if (Array.isArray(list) && list.length) {
            setAccessModules(setRuntimeAccessModules(list, ACCESS_CATALOG_STATES.LIVE));
            setAccessCatalogStatus(ACCESS_CATALOG_STATES.LIVE);
            return;
        }
        // Fetched, but zero rows: distinct from "never fetched" — the API is reachable and
        // the catalog is genuinely empty, so nothing an admin edits here is in play yet.
        setAccessModules(setRuntimeAccessModules(null, ACCESS_CATALOG_STATES.EMPTY));
        setAccessCatalogStatus(ACCESS_CATALOG_STATES.EMPTY);
    };

    const markCatalogUnavailable = () => {
        // Leave whatever catalog is already in memory alone; only the provenance changes.
        setAccessCatalogState(ACCESS_CATALOG_STATES.UNAVAILABLE);
        setAccessCatalogStatus(ACCESS_CATALOG_STATES.UNAVAILABLE);
    };

    const refreshAccessCatalog = async () => {
        if (!token && !localStorage.getItem(config.key.token)) {
            markCatalogUnavailable();
            return getAccessModules();
        }
        try {
            const response = await fetchAccessModules({ application: 'care-admin' });
            const rows = response?.data ?? response;
            const list = Array.isArray(rows) ? rows : [];
            applyAccessCatalog(list);
            return getAccessModules();
        } catch (error) {
            // API not deployed / table missing — keep the built-in catalog.
            markCatalogUnavailable();
            console.warn('Access module catalog unavailable; using built-in list.', error?.message);
            return getAccessModules();
        }
    };

    const resolveExpiryTimestamp = (currentUser) => {
        if (!currentUser) return null;
        const rawExpiry =
            currentUser?.expireAt?.date ??
            currentUser?.expireAt ??
            currentUser?.expiry_date ??
            currentUser?.expiresAt ??
            currentUser?.exp ??
            null;

        if (rawExpiry == null) return null;

        // JWT exp is often in seconds; Date.now() is milliseconds
        if (typeof rawExpiry === 'number') {
            return rawExpiry < 1e12 ? rawExpiry * 1000 : rawExpiry;
        }

        const parsed = new Date(rawExpiry).getTime();
        return Number.isFinite(parsed) ? parsed : null;
    };

    const set = async (data) => {
        setToken(data?.jwt);

        // If profile data is included, extract permissions before publishing user
        // so ProtectedRoute does not fail-closed on an empty map mid-login.
        if (data?.profile) {
            const permMap = buildPermissionMap(data.profile);
            setPermissions(permMap);
            setUser(data);
            setProfileBootstrapped(true);
            refreshAccessCatalog();
            return;
        }

        if (data?.jwt) {
            try {
                const profileResponse = await fetchCurrentUserProfile(data.jwt);
                if (profileResponse?.data && !profileResponse?.error) {
                    const profile = profileResponse.data;
                    const permMap = buildPermissionMap(profile);
                    setPermissions(permMap);
                    setUser({ ...data, profile });
                    setProfileBootstrapped(true);
                    refreshAccessCatalog();
                    return;
                }
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
            }
        }

        // Profile unavailable — still sign in; legacy staffRoleId soft-fallback applies.
        // Bootstrap has RESOLVED (as a failure); gates must stop waiting, not spin forever.
        setPermissions({});
        setUser(data);
        setProfileBootstrapped(true);
    }

    const unset = async () => {
        profileRefreshOnce.current = false;
        permissionsVersionRef.current = null;
        setToken(null);
        setUser(null);
        setPermissions({});
        setProfileBootstrapped(false);
        setAccessModules(setRuntimeAccessModules(null, ACCESS_CATALOG_STATES.UNAVAILABLE));
        setAccessCatalogStatus(ACCESS_CATALOG_STATES.UNAVAILABLE);
        ClearValues();
    }
    
    // Refresh user profile and permissions (useful after role changes)
    const refreshProfile = async () => {
        if (!token) {
            // Nothing to fetch — bootstrap is resolved by definition.
            setProfileBootstrapped(true);
            return;
        }

        try {
            const profileResponse = await fetchCurrentUserProfile();
            if (profileResponse?.data && !profileResponse?.error) {
                const profile = profileResponse.data;
                const permMap = buildPermissionMap(profile);
                setPermissions(permMap);
                // Update user with fresh profile data
                const updatedUser = { ...user, profile };
                setUser(updatedUser);
                await refreshAccessCatalog();
                return profile;
            }
        } catch (error) {
            console.error('Failed to refresh user profile:', error);
            // If 401/403, might need to logout
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                unset();
            }
            throw error;
        } finally {
            // Resolved either way — a failed profile call must not leave gates spinning.
            setProfileBootstrapped(true);
        }
    }

    useEffect(() => {
        if (!token || !user) {
            // No session to bootstrap — resolved, so gates fall through to /login
            // instead of waiting on a fetch that will never run.
            setProfileBootstrapped(true);
            return undefined;
        }
        refreshAccessCatalog();
        // Always re-fetch profile once per session restore so newly granted
        // role permissions (e.g. preauth.decide) are not stuck in localStorage.
        if (!profileRefreshOnce.current) {
            profileRefreshOnce.current = true;
            const timer = setTimeout(() => {
                refreshProfile().catch((error) => {
                    console.error('Failed to bootstrap profile:', error);
                });
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [token, user?.jwt]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(user){
            const expiryTimestamp = resolveExpiryTimestamp(user);
            if (!expiryTimestamp) {
                return undefined;
            }
            timerRef.current = setInterval(() => {
                if((Date.now() > expiryTimestamp)){
                    if(timerRef.current) clearInterval(timerRef.current)
                    unset()
                    window.location.assign('/login?reason=session_expired');
                }
            }, 1000)
        }
        
        // Listen for permissions updates (e.g., after role changes)
        const handlePermissionsUpdate = (event) => {
            if (event.detail?.permissions) {
                setPermissions(event.detail.permissions);
            }
        };

        // Listen for 403 errors to refresh profile
        const handleRefreshOn403 = async () => {
            if (token) {
                try {
                    await refreshProfile();
                } catch (error) {
                    console.error('Failed to refresh profile on 403:', error);
                }
            }
        };

        // The WS server delivers to every connected admin client regardless
        // of the recipient it was addressed to (care-admin connections see
        // everything — see WebSocketServer::shouldSendTo, app === 'admin'
        // short-circuits before filters are checked). So the match against
        // "is this event actually about me" has to happen here, not on the
        // server. Same three id shapes dash/index.js already checks for
        // "is the account I just edited my own account".
        const isCurrentUser = (userId, userType) => {
            if (!userId) return false;
            const matchesId =
                Number(userId) === Number(user?.id)
                || Number(userId) === Number(user?.rbac_user_id)
                || Number(userId) === Number(user?.profile?.user?.id);
            if (!matchesId) return false;
            if (!userType) return true;
            const myType = user?.type || user?.profile?.user?.user_type || user?.user_type;
            return !myType || String(userType) === String(myType);
        };

        const handleRbacPermissionsChanged = async (event) => {
            const { userId, userType } = event.detail || {};
            if (!isCurrentUser(userId, userType)) return;
            try {
                await refreshProfile();
            } catch (error) {
                console.error('Failed to refresh profile on live permissions change:', error);
            }
        };

        window.addEventListener('permissionsUpdated', handlePermissionsUpdate);
        window.addEventListener('refreshProfileOn403', handleRefreshOn403);
        window.addEventListener('rbacPermissionsChanged', handleRbacPermissionsChanged);

        // Fallback / guaranteed-delivery path: poll a cheap version stamp on
        // the same cadence as the app's existing HTTP notification poll.
        // Catches everything the WS push would (it's a superset — the push
        // is purely a latency optimization on top of this), including
        // periods where no live socket is connected at all, which today is
        // the default (care-admin runs notifications in 'http' mode).
        const pollPermissionsVersion = async () => {
            if (!token) return;
            try {
                const { version } = (await fetchPermissionsVersion()) || {};
                if (version == null) return;
                if (permissionsVersionRef.current === null) {
                    // First observation this session — nothing to compare
                    // against yet, just record it.
                    permissionsVersionRef.current = version;
                    return;
                }
                if (version !== permissionsVersionRef.current) {
                    permissionsVersionRef.current = version;
                    await refreshProfile();
                }
            } catch (error) {
                // Never let a poll failure interrupt the session — this is
                // an optimization, not a requirement for the app to work.
                console.warn('Permissions version poll failed:', error?.message);
            }
        };
        if (token) {
            // Deliberately NOT invoked immediately: this effect re-runs on
            // every `user` reference change, and refreshProfile() (called
            // below, and by the 403 handler above) produces a new `user`
            // object on every success. An immediate call here would fire a
            // fresh network request every time refreshProfile() runs anywhere
            // in the app, including in response to THIS poll's own refresh —
            // a self-sustaining loop under any transient 401 (e.g. the known
            // just-after-login token race the bootstrap effect above already
            // works around with its own 100ms delay). The interval alone is
            // enough for a poll; nothing here needs to run before the first
            // tick.
            permissionsPollRef.current = setInterval(pollPermissionsVersion, 30000);
        }

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (permissionsPollRef.current) clearInterval(permissionsPollRef.current);
            window.removeEventListener('permissionsUpdated', handlePermissionsUpdate);
            window.removeEventListener('refreshProfileOn403', handleRefreshOn403);
            window.removeEventListener('rbacPermissionsChanged', handleRbacPermissionsChanged);
        };
    }, [user]) //eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AuthContext.Provider value={{ set, unset, token, user, permissions, profileBootstrapped, refreshProfile, accessModules, accessCatalogState, refreshAccessCatalog }}>
            {children}
        </AuthContext.Provider>
    )
}